import React, { useState, useEffect, useMemo } from 'react';
import apiClient from '../services/api';
import './LogsPage.css';

const PAGE_SIZES = [10, 25, 50, 100];

function SortIcon({ col, sort }) {
  if (sort.col !== col) return <span className="sort-icon inactive">⇅</span>;
  return <span className="sort-icon active">{sort.dir === 'asc' ? '↑' : '↓'}</span>;
}

function useSortable(col, dir = 'desc') {
  const [sort, setSort] = useState({ col, dir });
  const toggle = (c) => setSort(p => ({ col: c, dir: p.col === c && p.dir === 'desc' ? 'asc' : 'desc' }));
  return [sort, toggle];
}

function Th({ col, label, sort, onSort }) {
  return (
    <th className="sortable-th" onClick={() => onSort(col)}>
      {label} <SortIcon col={col} sort={sort} />
    </th>
  );
}

const accessor = (row, col) => {
  switch (col) {
    case 'timestamp': return row.timestamp;
    case 'service': return row.service;
    case 'message': return row.message;
    case 'statusCode': return row.statusCode;
    case 'userId': return row.userId;
    default: return row[col];
  }
};

function LogsPage() {
  const [sort, toggleSort] = useSortable('timestamp', 'desc');
  const [page, setPage] = useState(1);
  const [size, setSize] = useState(25);
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [expandedId, setExpandedId] = useState(null);

  useEffect(() => {
    const loadLogs = async () => {
      setLoading(true);
      try {
        const response = await apiClient.get('/api/logs');
        setLogs(response.data.logs || []);
      } catch (err) {
        console.error('Failed to fetch logs:', err);
      } finally {
        setLoading(false);
      }
    };
    loadLogs();
  }, []);

  const handleRefresh = async () => {
    setLoading(true);
    try {
      const response = await apiClient.get('/logs');
      setLogs(response.data.logs || []);
    } catch (err) {
      console.error('Failed to refresh logs:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleClearLogs = async () => {
    if (!window.confirm('Are you sure you want to delete all logs?')) return;
    try {
      await apiClient.delete('/logs');
      setLogs([]);
      setPage(1);
    } catch (err) {
      console.error('Failed to clear logs:', err);
    }
  };

  const sortedLogs = useMemo(() => {
    return [...logs].sort((a, b) => {
      const av = accessor(a, sort.col) ?? -Infinity;
      const bv = accessor(b, sort.col) ?? -Infinity;
      if (av === bv) return 0;
      return (av < bv ? -1 : 1) * (sort.dir === 'asc' ? 1 : -1);
    });
  }, [logs, sort]);

  const pages = Math.max(1, Math.ceil(sortedLogs.length / size));
  const rows = sortedLogs.slice((page - 1) * size, page * size);

  const getStatusColor = (code) => {
    if (code >= 500) return '#ef4444';
    if (code >= 400) return '#f97316';
    return '#10b981';
  };

  return (
    <div className="logs-page">
      <div className="logs-section">
        <div className="section-header">
          <div>
            <h2 className="section-title">Error Logs</h2>
            <p className="section-subtitle">Latest {logs.length} errors (max 100)</p>
          </div>
          <div className="header-actions">
            <button
              className="refresh-btn"
              onClick={handleRefresh}
              disabled={loading}
              title="Refresh logs"
            >
              {loading ? 'Loading…' : '🔄'}
            </button>
            <button
              className="clear-btn"
              onClick={handleClearLogs}
              disabled={logs.length === 0}
              title="Clear all logs"
            >
              🗑️ Clear
            </button>
          </div>
        </div>

        {logs.length === 0 ? (
          <div className="section-empty">
            <p>No errors logged yet</p>
          </div>
        ) : (
          <>
            <div className="logs-table-wrap">
              <table className="logs-table">
                <thead>
                  <tr>
                    <th>Details</th>
                    <Th col="timestamp" label="Timestamp" sort={sort} onSort={toggleSort} />
                    <Th col="service" label="Service/Route" sort={sort} onSort={toggleSort} />
                    <Th col="statusCode" label="Status" sort={sort} onSort={toggleSort} />
                    <Th col="message" label="Error Message" sort={sort} onSort={toggleSort} />
                    <Th col="userId" label="User" sort={sort} onSort={toggleSort} />
                  </tr>
                </thead>
                <tbody>
                  {rows.map((log, i) => (
                    <React.Fragment key={log.id}>
                      <tr className="log-row">
                        <td className="details-cell">
                          <button
                            className="expand-btn"
                            onClick={() => setExpandedId(expandedId === log.id ? null : log.id)}
                            title={log.stack ? 'View stack trace' : 'No stack trace'}
                            disabled={!log.stack}
                          >
                            {expandedId === log.id ? '▼' : '▶'}
                          </button>
                        </td>
                        <td className="timestamp-cell">
                          {new Date(log.timestamp).toLocaleString()}
                        </td>
                        <td className="service-cell">{log.service}</td>
                        <td className="status-cell">
                          <span className="status-badge" style={{ backgroundColor: getStatusColor(log.statusCode) }}>
                            {log.statusCode}
                          </span>
                        </td>
                        <td className="message-cell">{log.message}</td>
                        <td className="user-cell">{log.userId}</td>
                      </tr>
                      {expandedId === log.id && log.stack && (
                        <tr className="expanded-row">
                          <td colSpan="6">
                            <div className="stack-trace">
                              <strong>Stack Trace:</strong>
                              <pre>{log.stack}</pre>
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="logs-footer">
              <span className="page-range">
                {Math.min((page - 1) * size + 1, sortedLogs.length)}–{Math.min(page * size, sortedLogs.length)} of {sortedLogs.length}
              </span>
              <select
                className="page-size-select"
                value={size}
                onChange={(e) => {
                  setSize(Number(e.target.value));
                  setPage(1);
                }}
              >
                {PAGE_SIZES.map((s) => (
                  <option key={s} value={s}>
                    {s} / page
                  </option>
                ))}
              </select>
              {pages > 1 && (
                <div className="pagination">
                  <button disabled={page === 1} onClick={() => setPage(1)}>
                    «
                  </button>
                  <button disabled={page === 1} onClick={() => setPage((p) => p - 1)}>
                    ‹
                  </button>
                  <span className="page-info">
                    {page} / {pages}
                  </span>
                  <button disabled={page === pages} onClick={() => setPage((p) => p + 1)}>
                    ›
                  </button>
                  <button disabled={page === pages} onClick={() => setPage(pages)}>
                    »
                  </button>
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
}

export default LogsPage;
